<template>
  <div class="main-cart__summary cart-summary critical-component-clear">
    <div
      v-if="savings > 0"
      class="cart-summary__savings-breakdown text-body-xs text-body-s-desktop"
    >
      <div class="cart-summary__savings-item">
        <span
          class="cart-summary__savings-label--original-price"
          v-text="$string('cart.summary.original_price')"
        />
        <span
          class="cart-summary__savings-value product-prices__compare-at-price"
          v-text="$formatMoney(listPriceTotal)"
        />
      </div>

      <div
        class="cart-summary__savings-item
              cart-summary__savings-item--total"
      >
        <span
          class="cart-summary__savings-label--total-saving"
          v-text="$string('cart.summary.total-saving')"
        />
        <span
          class="cart-summary__savings-value product-prices__discount"
          v-text="`-${$formatMoney(savings)}`"
        />
      </div>
    </div>

    <div
      v-if="savings > 0"
      class="cart-summary__divider"
    />

    <div class="cart-summary__subtotal-container">
      <div class="cart-summary__subtotal-wrapper">
        <h2
          class="cart-summary__subtotal text-heading-s text-heading-m-desktop"
          v-text="$string('cart.summary.subtotal')"
        />
        <p
          class="text-body-2xs text-body-xs-desktop"
          v-text="freeDelivery
            ? $string('cart.summary.free_delivery')
            : $string('cart.summary.excluding_delivery')"
        />
      </div>

      <p
        class="cart-summary__title text-heading-s text-heading-m-desktop"
        v-text="$formatMoney(cart.total_price)"
      />
    </div>

    <div class="cart-summary__divider" />

    <gift-wrap-item
      v-if="giftWrapping.enable && giftWrapping.product.handle"
      :product="giftWrapping.product"
    />

    <div
      v-if="giftWrapping.enable && giftWrapping.product.handle"
      class="cart-summary__divider"
    />

    <div class="cart-summary__payments">
      <btn
        class="cart-summary__submit"
        :label="$string('cart.go_to_checkout')"
        modifiers="large block"
        :show-icon="false"
        name="checkout"
        type="submit"
      />

      <div
        ref="additionalCheckoutButtons"
        class="cart-summary__additional-checkout-buttons"
      />
    </div>

    <div class="cart-summary__below-buttons">
      <div class="cart-summary__payments">
        <klarna-messaging
          class="cart-summary__klarna"
          namespace="cart-summary"
          :price="cart.total_price"
        />

        <afterpay-messaging
          class="cart-summary__afterpay"
          namespace="cart-summary"
          :price="cart.total_price"
        />
      </div>
    </div>

    <div class="cart-summary__divider" />

    <div
      v-if="navigationItems.length"
      class="cart-summary__navigation-items"
    >
      <a
        v-for="item of navigationItems"
        :key="item.title"
        class="
          cart-summary__navigation-item
          text-body-xs-link
          text-body-s-link-desktop
        "
        :href="item.url"
        v-text="item.title"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart summary (main-cart)
 * -----------------------------------------------------------------------------
 * Summary of cart metrics with checkout CTA.
 *
 * @param {Object} giftWrapping - Object of gift wrapping feature.
 * @param {Boolean} freeDelivery - Is free delivery available.
 * @param {Array} navigationItems - Array of navigation link for the cart.
 * @param {Array} payments - Array of payment icons and type.
 *
 */
import { mapState } from 'vuex'

import AfterpayMessaging from '~async/afterpay-messaging/afterpay-messaging'
import Btn from '~global/btn/btn'
import GiftWrapItem from '~async/gift-wrap-item/gift-wrap-item'
import KlarnaMessaging from '~async/klarna-messaging/klarna-messaging'

import { moveAsyncElement } from '~/helpers/general'

export default {
  name: 'CartSummary',

  components: {
    AfterpayMessaging,
    Btn,
    GiftWrapItem,
    KlarnaMessaging,
  },

  props: {
    navigationItems: {
      type: Array,
      default: () => [],
    },
    freeDelivery: {
      type: Boolean,
      default: false,
    },
    giftWrapping: {
      type: Object,
      default: () => ({
        enable: false,
        product: {},
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      nextDayDeliverySettings: (state) => state['next-day-shipping'].settings,
    }),

    /**
     * Compute list price total (original prices).
     * @returns {Number}
     */
    listPriceTotal() {
      let total = 0

      this.cart.items.forEach((item) => {
        if (item.compare_at_line_price) {
          total += item.compare_at_line_price
        } else {
          total += item.final_line_price
        }
      })

      return total
    },

    /**
     * Compute money saved.
     * @returns {Number}
     */
    savings() {
      let savings = 0

      this.cart.items.forEach((item) => {
        if (!item.compare_at_line_price) {
          return
        }

        const difference = item.compare_at_line_price - item.final_line_price

        if (difference <= 0) {
          return
        }

        savings += difference
      })

      return savings
    },
  },

  mounted() {
    moveAsyncElement(
      '[js-additional-checkout-buttons="container"]',
      this.$refs.additionalCheckoutButtons,
    )
  },
}
</script>

<style lang="scss">
@import './cart-summary';
</style>
